import { DandelionService, FlowError, FlowErrorType, StorageError, StorageErrorType, TranslationService } from '@dandelion/core';
import { injectable } from 'inversify';
import { reactive, Reactive, readonly, UnwrapNestedRefs } from 'vue';
import { UserService } from './UserService';
import { Preference, PreferenceValue } from '../entities/Preference';
import { Preferences } from '../enum/Preferences';
import { Users } from '../enum/Users';
import { PreferencesStorageInterface } from '../interfaces/PreferencesStorageInterface';

@injectable()
export class PreferencesService extends DandelionService {
    protected currentPreferences: Reactive<PreferenceValue[]> | undefined = undefined;

    constructor(protected userService: UserService, protected ts: TranslationService, protected preferencesStorage: PreferencesStorageInterface) {
        super();
    }

    async onPreferenceClick(preferenceName: Preferences, showPanel: (name: string) => void) {
        const preferenceToSet = await this.getPreference(preferenceName);
        const toggleValue = (value: string, option1: string, option2: string) =>
            value === option1 ? option2 : option1;

        switch (preferenceToSet.preferenceName) {
            case Preferences.DEBUG:
            case Preferences.STATISTICIAN:
            case Preferences.ASKCONSENT:
                const newValue = toggleValue(preferenceToSet.value, 'false', 'true');
                this.setPreference(preferenceName, newValue);
                break;

            /*case Preferences.THEME:
                preferenceToSet.value = toggleValue(preferenceToSet.value, 'light', 'dark');
                this.setPreference(preferenceToSet)
                break;*/

            case Preferences.USERCONTROLPANEL:
                if (
                    this.userService.getCurrentUser().value?.role === Users.SUPPORT_ADMIN ||
                    this.userService.getCurrentUser().value?.role === Users.ADMIN
                ) {
                    window.location.href = `${import.meta.env.VITE_APP_AUTH_BASE_URL}UserManager`;
                }
                break;

            case Preferences.TYPEIMPORT:
            case Preferences.GCPSTRATEGY:
            case Preferences.CONNECTORS:
                showPanel(preferenceToSet.preferenceName);
                break;
        }
    }

    async init() {
        this.currentPreferences = reactive(await this.preferencesStorage.getPreferences());
    }

    getPreferences(): Readonly<UnwrapNestedRefs<PreferenceValue[]>> {
        if (!this.currentPreferences) {
            throw new FlowError('Preferences not found in preferencesService, `init` method must be called first', FlowErrorType.CURRENT_CASE_NOT_FOUND);
        }
        return readonly(this.currentPreferences);
    }

    getPreference(name: string): PreferenceValue {
        const preferenceValues = this.getPreferences();
        const preferenceValue = preferenceValues.find((prefValue: PreferenceValue) => prefValue.preferenceName == name);
        if (!preferenceValue) {
            //TODO - Subsitute this StorageErrorType with an appropriate one when new Core version
            throw new StorageError(`Preference with name: ${name} doesn\'t exist in saved preferences`, StorageErrorType.CASE_METADATA_NOT_FOUND);
        }
        return preferenceValue;
    }

    async setPreference(name: Preferences, value: string) {
        const newValue: PreferenceValue = { preferenceName: name, value };
        const preferencesValues = this.getPreferences();
        const index = preferencesValues.findIndex((prefValue: PreferenceValue) => prefValue.preferenceName === name);
        if (this.currentPreferences && index != -1) {
            this.currentPreferences.splice(index, 1, newValue);
        }
        await this.preferencesStorage.savePreference(newValue);
    }
}
